/**
 * KetoFusionDashboard — daily mission hub for enrolled users.
 * Route: /keto-fusion/today
 *
 * Reads GET /api/keto-fusion/today + /api/keto-fusion/progress.
 * Shows: 4-block progress bar, current week/day, today's mission card
 * (gender-aware), training & fueling hints, complete CTA.
 */

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Flame, Check, Sparkles, Target, Dumbbell, Apple, ArrowRight, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const BLOCK_THEME = {
  1: { color: "#84cc16", name: "Foundation" },
  2: { color: "#f97316", name: "Carb Cycling" },
  3: { color: "#a855f7", name: "Performance" },
  4: { color: "#22c55e", name: "Solidification" },
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export default function KetoFusionDashboard() {
  const navigate = useNavigate();
  const [today, setToday] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [celebrating, setCelebrating] = useState(false);

  const load = async () => {
    try {
      const [tRes, pRes] = await Promise.all([
        fetch(`${API}/api/keto-fusion/today`, { headers: authHeaders() }).then((r) => r.json()),
        fetch(`${API}/api/keto-fusion/progress`, { headers: authHeaders() }).then((r) => r.json()),
      ]);
      setToday(tRes);
      setProgress(pRes);
    } catch (e) {
      toast.error("Erro ao carregar a tua missão");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Redirect to unified PhaseExecute — consolidate all Fusion dashboards
  useEffect(() => {
    if (!loading) {
      navigate("/programs/keto_fusion/phase", { replace: true });
    }
  }, [loading, navigate]);

  // Auto-redirect to Finale when program is complete
  useEffect(() => {
    if (today?.enrolled && today?.status === "completed") {
      navigate("/keto-fusion/finale", { replace: true });
    }
  }, [today, navigate]);

  const completeMission = async () => {
    if (!today?.mission) return;
    setCompleting(true);
    try {
      const res = await fetch(`${API}/api/keto-fusion/complete-mission`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ week: today.current_week, day: today.current_day }),
      });
      const data = await res.json();
      if (data?.success) {
        setCelebrating(true);
        setTimeout(() => {
          setCelebrating(false);
          setToday(data.next);
          load();
        }, 1800);
      }
    } catch (e) {
      toast.error("Erro a marcar completa");
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}>
          <Flame className="w-10 h-10 text-orange-400" />
        </motion.div>
      </div>
    );
  }

  if (!today?.enrolled) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <Card className="bg-zinc-950 border-orange-500/30 max-w-md">
          <CardContent className="p-8 text-center">
            <Flame className="w-12 h-12 text-orange-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-zinc-100 mb-2">Ainda não estás inscrita/o no Keto Fusion</h2>
            <p className="text-sm text-zinc-400 mb-6">12 semanas. 84 missões. Performance híbrida.</p>
            <Button onClick={() => navigate("/keto-fusion")} className="bg-orange-500 hover:bg-orange-600 text-white">
              Conhecer o programa
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const m = today.mission || {};
  const block = today.block || 1;
  const theme = BLOCK_THEME[block];
  const bodyText = m.body_gendered || m.body || "";

  return (
    <div className="min-h-screen bg-black text-zinc-100 relative overflow-x-hidden">
      {/* Ambient pulses */}
      <div className="fixed top-0 left-0 w-1/2 h-screen pointer-events-none opacity-30">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-[140px] bg-lime-500/30 animate-pulse" />
      </div>
      <div className="fixed top-0 right-0 w-1/2 h-screen pointer-events-none opacity-30">
        <div className="absolute top-1/2 right-1/4 w-96 h-96 rounded-full blur-[140px] bg-purple-500/30 animate-pulse" style={{ animationDelay: "1.5s" }} />
      </div>

      <div className="max-w-4xl mx-auto px-5 py-6 relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate("/keto-fusion")} className="text-zinc-400 hover:text-zinc-200 flex items-center gap-1 text-sm" data-testid="fusion-back">
            <ChevronLeft className="w-4 h-4" /> Keto Fusion
          </button>
          <Badge className="bg-zinc-900 border border-zinc-800 text-zinc-300">
            <Flame className="w-3 h-3 mr-1 text-orange-400" /> {today.completion_pct || 0}% completo
          </Badge>
        </div>

        {/* Week / Day / Block hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex items-baseline gap-3 mb-2">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Semana</span>
            <span
              className="text-5xl font-black tracking-tight"
              style={{ color: theme.color, textShadow: `0 0 20px ${theme.color}55` }}
              data-testid="fusion-current-week"
            >
              {today.current_week}
            </span>
            <span className="text-2xl text-zinc-700 font-light">/</span>
            <span className="text-2xl text-zinc-600 font-light">12</span>
            <span className="text-zinc-500 mx-2">·</span>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Dia</span>
            <span className="text-3xl font-bold text-zinc-200">{today.current_day}</span>
            <span className="text-zinc-700">/7</span>
          </div>
          <p className="text-xs uppercase tracking-widest" style={{ color: theme.color }}>
            Bloco {block} · {theme.name}
          </p>
          {today.behind_days > 0 && (
            <p className="text-[11px] text-amber-400 mt-1">⏰ {today.behind_days} dia(s) atrás do ritmo calendário</p>
          )}
        </motion.div>

        {/* 4-block progress bar */}
        <div className="mb-8" data-testid="fusion-progress-blocks">
          <div className="grid grid-cols-4 gap-1">
            {[1, 2, 3, 4].map((b) => {
              const t = BLOCK_THEME[b];
              const done = progress?.per_block_done?.[b] || 0;
              const total = progress?.per_block_total?.[b] || 21;
              const pct = (done / total) * 100;
              const isCurrent = b === block;
              return (
                <div key={b} className="space-y-1">
                  <p className="text-[9px] uppercase tracking-widest text-zinc-500">Bloco {b}</p>
                  <div className="h-2 rounded-full bg-zinc-900 overflow-hidden relative">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: t.color, boxShadow: isCurrent ? `0 0 8px ${t.color}` : "none" }}
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 1, delay: b * 0.1 }}
                    />
                  </div>
                  <p className="text-[10px] font-mono text-zinc-400">{done}/{total}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Today's mission card */}
        <AnimatePresence mode="wait">
          {celebrating ? (
            <motion.div
              key="celebration"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-zinc-950 border rounded-2xl p-10 text-center"
              style={{ borderColor: `${theme.color}55`, boxShadow: `0 0 60px ${theme.color}40` }}
              data-testid="fusion-celebration"
            >
              <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1, repeat: Infinity }}>
                <Check className="w-16 h-16 mx-auto mb-3" style={{ color: theme.color }} />
              </motion.div>
              <h3 className="text-2xl font-bold text-zinc-100 mb-1">Missão completa</h3>
              <p className="text-sm text-zinc-400">Próxima missão a carregar...</p>
            </motion.div>
          ) : (
            <motion.div
              key={`${today.current_week}-${today.current_day}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative rounded-2xl border bg-zinc-950 overflow-hidden"
              style={{ borderColor: `${theme.color}40`, boxShadow: `0 12px 40px ${theme.color}25` }}
              data-testid="fusion-mission-card"
            >
              {/* Background glow */}
              <motion.div
                className="absolute -top-20 -right-20 w-64 h-64 rounded-full pointer-events-none"
                style={{ background: theme.color, filter: "blur(80px)", opacity: 0.2 }}
                animate={{ scale: [1, 1.15, 1], opacity: [0.15, 0.3, 0.15] }}
                transition={{ duration: 5, repeat: Infinity }}
              />

              <div className="relative p-6 sm:p-8">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: `${theme.color}20`, border: `1px solid ${theme.color}55` }}>
                    <Target className="w-4 h-4" style={{ color: theme.color }} />
                  </div>
                  <p className="text-[10px] uppercase tracking-widest" style={{ color: theme.color }}>
                    Missão de hoje · {m.focus}
                  </p>
                </div>

                <h2 className="text-3xl sm:text-4xl font-black text-zinc-100 mb-4 tracking-tight" data-testid="fusion-mission-title">
                  {m.title}
                </h2>

                <p className="text-base text-zinc-200 leading-relaxed mb-6" data-testid="fusion-mission-body">
                  {bodyText}
                </p>

                {(m.training_hint || m.fueling_hint) && (
                  <div className="space-y-2 mb-6">
                    {m.training_hint && (
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-zinc-900/60 border border-zinc-800">
                        <Dumbbell className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-zinc-300">{m.training_hint}</p>
                      </div>
                    )}
                    {m.fueling_hint && (
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-zinc-900/60 border border-zinc-800">
                        <Apple className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-zinc-300">{m.fueling_hint}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Spirit tone hint */}
                <div className="flex items-center gap-2 mb-6 text-xs text-zinc-500">
                  <Sparkles className="w-3 h-3 text-purple-400" />
                  <span>Spirit · tom {m.spirit_tone || "supportive"}</span>
                </div>

                <Button
                  onClick={completeMission}
                  disabled={completing}
                  className="w-full bg-gradient-to-r from-lime-500 to-purple-500 hover:from-lime-400 hover:to-purple-400 text-white font-bold text-base h-12 shadow-lg shadow-purple-500/30"
                  data-testid="fusion-complete-btn"
                >
                  {completing ? "A registar..." : (
                    <>
                      <Check className="w-5 h-5 mr-2" /> Marcar missão concluída <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer stats */}
        {progress && (
          <div className="mt-6 grid grid-cols-3 gap-3" data-testid="fusion-stats">
            <Stat label="Missões feitas" value={`${progress.missions_done}/${progress.missions_total}`} color="#84cc16" />
            <Stat label="Bloco atual" value={`${block}/4`} color={theme.color} />
            <Stat label="Estado" value={progress.status === "completed" ? "Concluído" : "Ativo"} color="#a855f7" />
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-center">
      <p className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1">{label}</p>
      <p className="text-lg font-bold font-mono" style={{ color }}>{value}</p>
    </div>
  );
}
